import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, Settings, Plus, Trash2, Edit2, BarChart3,
  Users, Send, Clock, Bot, Search, Phone
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const WhatsAppBotAdmin = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [settings, setSettings] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, statsRes, convoRes] = await Promise.all([
        api.get('/admin/whatsapp-bot/settings'),
        api.get('/admin/whatsapp-bot/stats'),
        api.get('/admin/whatsapp-bot/conversations?limit=50')
      ]);
      setSettings(settingsRes.data.settings);
      setQuickReplies(settingsRes.data.quick_replies);
      setStats(statsRes.data);
      setConversations(convoRes.data.conversations);
    } catch (error) {
      toast.error('Failed to load WhatsApp bot data');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async () => {
    try {
      await api.put('/admin/whatsapp-bot/settings', settings);
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="whatsapp-bot-admin">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <MessageSquare className="w-8 h-8 text-green-500" />
              WhatsApp Bot
            </h1>
            <p className="text-muted-foreground">AI-powered auto-responder for WhatsApp</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label="Unique Users" value={stats.unique_users} color="green" />
            <StatCard icon={MessageSquare} label="Total Conversations" value={stats.total_conversations} color="blue" />
            <StatCard icon={Bot} label="AI Responses" value={stats.ai_responses} color="purple" />
            <StatCard icon={Clock} label="Today" value={stats.today_conversations} color="yellow" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 mb-6">
          {['overview', 'quick-replies', 'conversations', 'settings'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 capitalize transition-colors ${
                activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.replace('-', ' ')}
            </button>
          ))}
        </div>

        {/* Overview */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4">How It Works</h3>
              <div className="space-y-3 text-muted-foreground">
                <p>1. Customer clicks WhatsApp link on your site</p>
                <p>2. Pre-filled message opens in WhatsApp</p>
                <p>3. AI (like Priya) auto-responds to queries</p>
                <p>4. Quick replies for common questions</p>
              </div>
            </div>
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4">WhatsApp Link</h3>
              <p className="text-sm text-muted-foreground mb-4">Share this link for customers to chat:</p>
              {settings?.whatsapp_number ? (
                <div className="p-3 bg-white/5 rounded-lg break-all">
                  https://wa.me/91{settings.whatsapp_number}
                </div>
              ) : (
                <p className="text-yellow-500">Please configure WhatsApp number in Settings</p>
              )}
            </div>
          </div>
        )}

        {/* Quick Replies */}
        {activeTab === 'quick-replies' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-muted-foreground">{quickReplies.length} quick replies</p>
              <button className="btn-primary flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add Quick Reply
              </button>
            </div>
            <div className="glass-card overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left p-4">Trigger Keywords</th>
                    <th className="text-left p-4">Response</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {quickReplies.map((qr, idx) => (
                    <tr key={idx} className="border-t border-white/5">
                      <td className="p-4">
                        <code className="text-sm bg-white/10 px-2 py-1 rounded">{qr.trigger}</code>
                      </td>
                      <td className="p-4 max-w-xs truncate">{qr.response}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${qr.is_active ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20'}`}>
                          {qr.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <button className="p-2 hover:bg-white/10 rounded"><Edit2 className="w-4 h-4" /></button>
                          <button className="p-2 hover:bg-red-500/20 text-red-500 rounded"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Conversations */}
        {activeTab === 'conversations' && (
          <div className="space-y-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by phone number..."
                className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
            <div className="glass-card overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left p-4">Phone</th>
                    <th className="text-left p-4">Message</th>
                    <th className="text-left p-4">Response</th>
                    <th className="text-left p-4">Type</th>
                    <th className="text-left p-4">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {conversations.map((conv, idx) => (
                    <tr key={idx} className="border-t border-white/5">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-green-500" />
                          {conv.phone_number}
                        </div>
                      </td>
                      <td className="p-4 max-w-xs truncate">{conv.user_message}</td>
                      <td className="p-4 max-w-xs truncate">{conv.bot_response}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${conv.response_type === 'ai' ? 'bg-purple-500/20 text-purple-500' : 'bg-blue-500/20 text-blue-500'}`}>
                          {conv.response_type === 'ai' ? 'AI' : 'Quick'}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {new Date(conv.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Settings */}
        {activeTab === 'settings' && settings && (
          <div className="max-w-2xl space-y-6">
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4">General Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label>Enable WhatsApp Bot</label>
                  <input
                    type="checkbox"
                    checked={settings.is_enabled}
                    onChange={(e) => setSettings({...settings, is_enabled: e.target.checked})}
                    className="toggle"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Bot Name</label>
                  <input
                    type="text"
                    value={settings.bot_name}
                    onChange={(e) => setSettings({...settings, bot_name: e.target.value})}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">WhatsApp Number (without +91)</label>
                  <input
                    type="text"
                    value={settings.whatsapp_number}
                    onChange={(e) => setSettings({...settings, whatsapp_number: e.target.value})}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                    placeholder="9876543210"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Welcome Message</label>
                  <textarea
                    value={settings.welcome_message}
                    onChange={(e) => setSettings({...settings, welcome_message: e.target.value})}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">AI System Prompt</label>
                  <textarea
                    value={settings.system_prompt}
                    onChange={(e) => setSettings({...settings, system_prompt: e.target.value})}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                    rows={6}
                  />
                </div>
              </div>
              <button onClick={updateSettings} className="btn-primary mt-6">
                Save Settings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    green: 'text-green-500 bg-green-500/20',
    blue: 'text-blue-500 bg-blue-500/20',
    purple: 'text-purple-500 bg-purple-500/20',
    yellow: 'text-yellow-500 bg-yellow-500/20'
  };

  return (
    <div className="glass-card p-4">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppBotAdmin;
